import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { joinLobby } from "src/store/lobby"
import { selectIsAuthenticated } from "src/store/user"
import { playerFacingError } from "src/utils/errorMessages"

import BaseLayout from "src/layouts/BaseLayout"

import "./LobbyInvite.css"

/* Where an invite link lands when you are not in that lobby yet.

   It used to land nowhere. `/lobby/:number` sat behind two guards — signed in, and *already a
   member of this lobby* — and anyone else was bounced to the home page with "You have no power
   here!". That is every person an invite has ever been sent to: the link worked only for the
   players who did not need it.

   So the guards are replaced by a question. Signed out, the invite says who invited you where and
   offers the way in; signed in, it just joins you.

   **The password is asked for only when the lobby wants one.** We do not publish whether a lobby
   has a password — nothing needs to know that before trying — so the first attempt is made without
   one and `LobbyIncorrectPassword` is what reveals the field. A lobby with no password therefore
   costs the invited player no clicks at all, and one with a password asks exactly once. */
const LobbyInvite = ({ number }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const [password, setPassword] = useState("")
  const [needsPassword, setNeedsPassword] = useState(false)
  const [refusal, setRefusal] = useState(null)
  const [joining, setJoining] = useState(false)

  // The no-password attempt is made once per sign-in, not once per render — otherwise every
  // refusal re-renders and tries again, which is a retry loop against somebody's lobby.
  const attempted = useRef(false)

  const attemptJoin = useCallback(
    async (withPassword) => {
      setJoining(true)
      setRefusal(null)
      try {
        await dispatch(joinLobby({ number, password: withPassword || null })).unwrap()
        // `setLobbies` puts the lobby in the store; the route renders the real lobby from here.
      } catch (rejected) {
        const code = rejected?.code
        if (code === "LobbyIncorrectPassword") {
          // The first refusal is the lobby telling us it is locked, not the player getting it
          // wrong — so say "this one needs a password" until they have actually tried one.
          setNeedsPassword(true)
          setRefusal(withPassword ? playerFacingError(code) : null)
        } else {
          setRefusal(playerFacingError(code ?? rejected?.detail))
        }
      } finally {
        setJoining(false)
      }
    },
    [dispatch, number]
  )

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return
    attempted.current = true
    attemptJoin(null)
  }, [isAuthenticated, attemptJoin])

  const openRegisterModal = useCallback(() => {
    ModalService.show("RegisterModal", {
      onLogin: () => {
        ModalService.hideAll()
        ModalService.show("LoginModal", { onRegister: openRegisterModal })
      },
    })
  }, [])

  const submitPassword = (event) => {
    event.preventDefault()
    if (!password.trim() || joining) return
    attemptJoin(password)
  }

  return (
    <BaseLayout>
      <div className="invite">
        <p className="p-mini invite-eyebrow">{t("invite.eyebrow")}</p>
        <h1 className="invite-title">{t("invite.title", { number })}</h1>

        {!isAuthenticated ? (
          <>
            <p className="invite-lead">{t("invite.signInFirst")}</p>
            <div className="invite-actions">
              <button type="button" onClick={openRegisterModal}>
                {t("invite.signIn")}
              </button>
            </div>
          </>
        ) : needsPassword ? (
          <form className="invite-form" onSubmit={submitPassword}>
            <p className="invite-lead">{t("invite.passwordLead")}</p>
            <label htmlFor="invite-password">{t("lobbyModals.passwordLabel")}</label>
            <input
              type="password"
              id="invite-password"
              className="form-input"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("lobbyModals.joinPasswordPlaceholder")}
            />
            {refusal ? <p className="errored-text invite-refusal" role="alert">{refusal}</p> : null}
            <div className="invite-actions">
              <button type="submit" disabled={joining || !password.trim()}>
                {joining ? t("invite.joining") : t("invite.join")}
              </button>
              <button type="button" className="quiet" onClick={() => navigate("/")}>
                {t("invite.backHome")}
              </button>
            </div>
          </form>
        ) : (
          <>
            <p className="invite-lead">{joining ? t("invite.joining") : refusal ?? t("invite.joining")}</p>
            {refusal ? (
              <div className="invite-actions">
                <button type="button" className="quiet" onClick={() => navigate("/")}>
                  {t("invite.backHome")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </BaseLayout>
  )
}

export default LobbyInvite
